import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, ArrowRight, AlertCircle } from 'lucide-react';
import { GoalInput } from '@/components/GoalInput';
import { ProgressBar } from '@/components/ProgressBar';
import { TurnstileWidget } from '@/components/TurnstileWidget';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LoadingOverlay } from '@/components/LoadingOverlay';
import { RateLimitErrorDisplay } from '@/components/RateLimitError';
import { storage } from '@/lib/storage';
import { api } from '@/lib/api';
import { RateLimitError, TurnstileError } from '@/lib/errors';

export default function GoalInputPage() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<string[]>(['', '', '']);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);

  // Check if running on localhost for development bypass
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

  useEffect(() => {
    // Load saved goals if any
    const savedGoals = storage.getGoals();
    if (savedGoals.length > 0) {
      setGoals(savedGoals);
    }
    storage.setCurrentStep('goals');

    // Auto-set dev bypass token for localhost
    if (isLocalhost) {
      setTurnstileToken('dev-bypass');
    }
  }, [isLocalhost]);

  const addGoal = () => {
    if (goals.length < 5) {
      setGoals([...goals, '']);
    }
  };

  const removeGoal = (index: number) => {
    if (goals.length > 3) {
      setGoals(goals.filter((_, i) => i !== index));
    }
  };

  const updateGoal = (index: number, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = value;
    setGoals(newGoals);
    // Auto-save to session storage
    storage.setGoals(newGoals.filter((g) => g.trim().length > 0));
  };

  const validateGoals = (): boolean => {
    const validGoals = goals.filter((g) => g.trim().length >= 3);
    if (validGoals.length < 3) {
      setError('Please enter at least 3 goals (minimum 3 characters each)');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    setRateLimitError(null);

    if (!validateGoals()) {
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security verification');
      return;
    }

    const validGoals = goals.filter((g) => g.trim().length >= 3);

    try {
      setLoading(true);
      const response = await api.analyzeGoals(validGoals, turnstileToken);

      // Save to storage
      storage.setGoals(validGoals);
      storage.setQuestions(response.questions);
      storage.setAnswers({});
      storage.setCurrentStep('questions');

      // Navigate to questions
      navigate('/questions');
    } catch (err) {
      console.error('Failed to analyze goals:', err);

      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else if (err instanceof TurnstileError) {
        setError('Security verification failed. Please refresh the page and try again.');
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to analyze goals. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const validGoalsCount = goals.filter((g) => g.trim().length >= 3).length;
  const canSubmit = validGoalsCount >= 3 && turnstileToken && !loading;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading overlay with fun messages */}
      <LoadingOverlay
        isLoading={loading}
        messages={[
          'Analyzing your goals...',
          'Generating smart questions...',
          'Finding patterns...',
          'Preparing insights...',
          'Almost ready...',
        ]}
      />

      <div className="section">
        <div className="container-tight">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar current={1} total={3} />
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="heading">What Are Your Goals?</h1>
            <p className="subheading">
              Enter 3-5 goals you'd like to achieve. Be specific about what success looks like.
            </p>
          </motion.div>

          {/* Error Display */}
          {rateLimitError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <RateLimitErrorDisplay error={rateLimitError} />
            </motion.div>
          )}

          {error && !rateLimitError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="error-message mb-6 flex items-start gap-3"
            >
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Goal Inputs */}
          <div className="card mb-6">
            <div className="space-y-6">
              {goals.map((goal, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <label className="label">
                    Goal {index + 1} {index < 3 && <span className="text-error-600">*</span>}
                  </label>
                  <GoalInput
                    value={goal}
                    onChange={(value) => updateGoal(index, value)}
                    onRemove={index >= 3 ? () => removeGoal(index) : undefined}
                    placeholder={`Example: ${
                      index === 0
                        ? 'Launch my online business'
                        : index === 1
                        ? 'Learn Spanish fluently'
                        : 'Get promoted at work'
                    }`}
                    autoFocus={index === 0}
                  />
                </motion.div>
              ))}
            </div>

            {goals.length < 5 && (
              <button
                onClick={addGoal}
                className="btn-secondary w-full mt-6 flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Another Goal (Optional)
              </button>
            )}

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Tip:</strong> Be specific! Instead of "Exercise more," try "Run 3 times
                per week for 30 minutes."
              </p>
            </div>
          </div>

          {/* Turnstile Widget - Only show in production */}
          {!isLocalhost && (
            <div className="mb-6">
              <TurnstileWidget
                onSuccess={setTurnstileToken}
                onError={() => setError('Security verification failed. Please try again.')}
              />
            </div>
          )}

          {/* Development Mode Indicator */}
          {isLocalhost && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-900">
                <strong>Development Mode:</strong> Turnstile verification is bypassed for localhost testing.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
              disabled={loading}
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="btn-primary btn-large group flex-grow md:flex-grow-0"
            >
              {loading ? (
                <LoadingSpinner inline />
              ) : (
                <>
                  Continue to Questions
                  <ArrowRight
                    className="ml-2 group-hover:translate-x-1 transition-transform"
                    size={20}
                  />
                </>
              )}
            </button>
          </div>

          {/* Goal Counter */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {validGoalsCount} of 3 minimum goals entered
              {validGoalsCount >= 3 && (
                <span className="text-success-600 font-semibold ml-2">✓ Ready to continue</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
